// src/belediye/components/halkMasasi/RecentLookups.js
import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ClockIcon, UserIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  selectRecentLookups,
  selectRecentServerLookups,
} from "../../redux/slices/belediyeHalkMasasiSlice";

//   Paylaşılan util'ler (tek kaynak): tarih render + tarih parse
import TrDateTime from "../utils/TrDateTime";
import { parseDateMaybe } from "../utils/dateTime";

import "./RecentLookups.css";

// ------------------------------------------------------------
// Debug (opsiyonel)
// ------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => {
  if (DEBUG) console.log("[RecentLookups]", ...args);
};

// ------------------------------------------------------------
// Helpers (KVKK-safe)
// ------------------------------------------------------------
// KVKK maskeleme/ayıklama domain yardımcıları (yerel kalır).
// Tarih parse/format ise paylaşılan util'e taşındı (parseDateMaybe + TrDateTime).
const safeText = (v) => String(v ?? "").trim();

const pick = (...vals) => {
  for (const v of vals) {
    const t = safeText(v);
    if (t) return t;
  }
  return "";
};

const pickCitizenId = (x) => {
  const direct =
    x?.citizenId ??
    x?.citizen_id ??
    x?.citizen?.id ??
    x?.citizen?.pk ??
    x?.citizen?.citizen_id ??
    null;
  if (direct == null) return null;
  const s = String(direct).trim();
  return s || null;
};

const maskPhone10 = (phone10) => {
  const p = String(phone10 || "").replace(/\D+/g, "");
  if (p.length !== 10) return "**********";
  return `${p.slice(0, 3)}*** **${p.slice(8, 10)}`;
};

const pickMaskedPhone = (x) => {
  // local storage: {phoneMasked}
  const localMasked = pick(x?.phoneMasked, x?.phone_masked);
  if (localMasked) return localMasked;

  // backend: citizen.masked_phone veya phone_normalized
  const backendMasked = pick(x?.citizen?.masked_phone, x?.masked_phone);
  if (backendMasked) return backendMasked;

  const phoneNorm = pick(x?.phone_normalized, x?.phone10, x?.phone);
  if (phoneNorm) {
    const digits = String(phoneNorm).replace(/\D+/g, "").slice(-10);
    return maskPhone10(digits);
  }

  return "**********";
};

const pickLastViewedAt = (x) =>
  pick(x?.lastViewedAt, x?.last_viewed_at, x?.lastViewed, x?.created_at, x?.createdAt);

const maskNamePart = (part) => {
  const s = safeText(part);
  if (!s) return "";
  const first = s.charAt(0).toLocaleUpperCase("tr-TR");
  return `${first}***`;
};

const pickMaskedName = (x) => {
  // backend citizen fields (tolerant)
  const c = x?.citizen || x?.citizen_detail || null;

  // eğer backend zaten maskeli dönüyorsa onu direkt kullan
  const alreadyMasked = pick(c?.masked_name, c?.masked_full_name, x?.masked_name);
  if (alreadyMasked) return alreadyMasked;

  const first = pick(c?.name, c?.first_name, x?.citizen_first_name, x?.first_name);
  const last = pick(c?.surname, c?.last_name, x?.citizen_last_name, x?.last_name);

  const maskedFirst = maskNamePart(first);
  const maskedLast = maskNamePart(last);

  const full = [maskedFirst, maskedLast].filter(Boolean).join(" ").trim();
  return full || "Vatandaş";
};

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
export default function RecentLookups({
  
  emptyText = "Henüz sorgu yapılmadı.",
  limit = 10,
  loading = false,
  onSelectCitizen,
}) {
  const navigate = useNavigate();

  //   Slice selectors (path uyumu için)
  const localItems = useSelector(selectRecentLookups) || [];
  const serverItems = useSelector(selectRecentServerLookups) || [];

  const items = useMemo(() => {
    const localArr = Array.isArray(localItems) ? localItems : [];
    const serverArr = Array.isArray(serverItems) ? serverItems : [];

    // unify
    const mappedServer = serverArr
      .map((x) => {
        const citizenId = pickCitizenId(x);
        if (!citizenId) return null;

        return {
          citizenId,
          nameMasked: pickMaskedName(x),
          phoneMasked: pickMaskedPhone(x),
          lastViewedAt: pickLastViewedAt(x),
          _src: "server",
        };
      })
      .filter(Boolean);

    const mappedLocal = localArr
      .map((x) => {
        const citizenId = pickCitizenId(x);
        if (!citizenId) return null;

        return {
          citizenId,
          nameMasked: "Vatandaş", // local storage KVKK: isim yok
          phoneMasked: pickMaskedPhone(x),
          lastViewedAt: pickLastViewedAt(x),
          _src: "local",
        };
      })
      .filter(Boolean);

    // merge + dedupe by citizenId (server prefer)
    const seen = new Set();
    const merged = [];

    for (const it of mappedServer) {
      if (seen.has(it.citizenId)) continue;
      seen.add(it.citizenId);
      merged.push(it);
    }
    for (const it of mappedLocal) {
      if (seen.has(it.citizenId)) continue;
      seen.add(it.citizenId);
      merged.push(it);
    }

    // sort newest first
    merged.sort((a, b) => {
      const ta = parseDateMaybe(a?.lastViewedAt)?.getTime?.() ?? 0;
      const tb = parseDateMaybe(b?.lastViewedAt)?.getTime?.() ?? 0;
      if (tb !== ta) return tb - ta;
      return String(a.citizenId).localeCompare(String(b.citizenId));
    });

    const sliced = merged.slice(0, Math.max(1, Number(limit) || 10));

    if (DEBUG) {
      log("render items", {
        serverCount: mappedServer.length,
        localCount: mappedLocal.length,
        mergedCount: merged.length,
        slicedCount: sliced.length,
        sample: sliced[0] || null,
      });
    }

    return sliced;
  }, [localItems, serverItems, limit]);

  const handleGo = useCallback(
    (citizenId) => {
      const cid = String(citizenId || "").trim();
      if (!cid) return;

      if (typeof onSelectCitizen === "function") {
        onSelectCitizen(cid);
        return;
      }

      navigate(`/yonetim/halk-masasi/vatandas/${cid}`, { replace: false });
    },
    [navigate, onSelectCitizen]
  );

  return (
    <section className="hm-recent" aria-label="Son sorgular">
      {/* Title (istersen sayfada ayrıca gösteriyorsun; burada da minimal bıraktım) */}
      

      {loading ? (
        <div className="hm-recent-state">
          <div className="hm-recent-skel">
            <div className="hm-recent-skel-row" />
            <div className="hm-recent-skel-row" />
            <div className="hm-recent-skel-row" />
          </div>
          <div className="hm-recent-stateText">Yükleniyor…</div>
        </div>
      ) : items.length === 0 ? (
        <div className="hm-recent-state">
          <div className="hm-recent-emptyIcon" aria-hidden="true">
            <UserIcon />
          </div>
          <div className="hm-recent-stateText">{emptyText}</div>
        </div>
      ) : (
        <div className="hm-recent-list" role="list">
          {items.map((it) => (
            <button
              key={String(it.citizenId)}
              type="button"
              className="hm-recent-item"
              onClick={() => handleGo(it.citizenId)}
              role="listitem"
            >
              <div className="hm-recent-left">
                <div className="hm-recent-avatar" aria-hidden="true">
                  <UserIcon />
                </div>

                <div className="hm-recent-main">
                  <div className="hm-recent-name">{it.nameMasked}</div>
                  <div className="hm-recent-phone">{it.phoneMasked}</div>

                  <div className="hm-recent-meta">
                    <ClockIcon className="hm-recent-metaIc" aria-hidden="true" />
                    <TrDateTime value={it.lastViewedAt} />
                    {DEBUG ? <span className="hm-recent-debug">({it._src})</span> : null}
                  </div>
                </div>
              </div>

              <div className="hm-recent-right" aria-hidden="true">
                <ChevronRightIcon className="hm-recent-chevron" />
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}